<!--
  Advisor (COD7) — con l'impaginazione della lista Clienti.

  Chi segue i partner per conto di Simba e matura una provvigione mensile su
  quello che producono.

  Cose non ovvie:
  1) NON SI CANCELLA, SI DISATTIVA. Le provvigioni dei mesi già calcolati
     restano legate all'advisor.
  2) I PARTNER SEGUITI SI SCRIVONO QUI. La M2M è dichiarata sull'advisor: il
     partner non la vede come relazione sua.
  3) È una risorsa Simba: con un utente non globale il backend risponde 403, e
     la pagina lo dice invece di mostrare una lista vuota.
  4) Gli advisor sono pochi: arrivano tutti in una chiamata, e le card in alto
     (tutti / attivi / disattivati) filtrano in pagina senza richiamare il server.
-->
<template>
  <div class="adv">
    <!-- Intestazione -->
    <div class="adv__head">
      <div>
        <h1 class="adv__title">
          Advisor
        </h1>
        <p class="adv__sub">
          Chi segue i partner e matura una provvigione mensile su quello che producono (COD7).
        </p>
      </div>
      <div class="adv__headActions">
        <el-input
          v-model="search"
          class="adv__search"
          placeholder="Cerca nome, cognome, mail…"
          clearable
          @input="onSearchInput"
        />
        <el-button
          type="primary"
          @click="openNew"
        >
          Nuovo advisor
        </el-button>
      </div>
    </div>

    <div
      v-if="forbidden"
      class="adv__warn"
    >
      <b>Sezione riservata a Simba.</b>
      Gli advisor e le loro provvigioni sono dati commerciali interni: il backend
      ne consente la gestione solo ai ruoli globali (superadmin).
    </div>

    <!-- KPI: sono anche il filtro dello stato -->
    <div class="adv__kpis">
      <button
        v-for="k in kpis"
        :key="k.key"
        class="adv__kpi"
        :class="{ 'is-active': segment === k.key, 'is-muted': k.key === 'disattivati' }"
        type="button"
        @click="segment = k.key"
      >
        <span class="adv__kpiNum">{{ k.value === null ? '—' : k.value }}</span>
        <span class="adv__kpiLbl">{{ k.label }}</span>
      </button>
    </div>

    <!-- Tabella -->
    <div class="adv__card">
      <el-table
        v-loading="loading"
        :data="visibleRows"
        style="width: 100%"
        :empty-text="emptyText"
        @row-click="openEdit"
      >
        <el-table-column
          label="Advisor"
          min-width="230"
        >
          <template #default="scope">
            <div class="adv__who">
              <span class="adv__avatar">{{ initials(scope.row) }}</span>
              <div>
                <div class="adv__name">
                  {{ fullName(scope.row) }}
                </div>
                <div class="adv__meta">
                  {{ scope.row.mail || '—' }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Telefono"
          min-width="140"
        >
          <template #default="scope">
            <span class="adv__num">{{ scope.row.phone_number || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Provvigione"
          width="130"
          align="right"
        >
          <template #default="scope">
            <span class="adv__num adv__strong">{{ percent(scope.row.commission_percent) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Partner seguiti"
          min-width="240"
        >
          <template #default="scope">
            <div
              v-if="(scope.row.partners || []).length"
              class="adv__chips"
            >
              <span
                v-for="p in scope.row.partners.slice(0, 3)"
                :key="p.id"
                class="adv__chip"
              >{{ p.company_name }}</span>
              <span
                v-if="scope.row.partners.length > 3"
                class="adv__chip adv__chip--more"
              >+{{ scope.row.partners.length - 3 }}</span>
            </div>
            <span
              v-else
              class="adv__dash"
            >Nessuno</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="140"
        >
          <template #default="scope">
            <StatusPill
              :kind="scope.row.is_active ? 'ok' : 'muted'"
              :label="scope.row.is_active ? 'Attivo' : 'Disattivato'"
            />
          </template>
        </el-table-column>

        <el-table-column
          label="Azioni"
          width="200"
          align="right"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              size="small"
              plain
              :loading="busyId === scope.row.id"
              @click.stop="toggleActive(scope.row)"
            >
              {{ scope.row.is_active ? 'Disattiva' : 'Riattiva' }}
            </el-button>
            <el-button
              type="primary"
              size="small"
              @click.stop="openEdit(scope.row)"
            >
              Modifica
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="adv__foot">
        <span class="adv__footInfo">
          {{ visibleRows.length }} advisor
          <template v-if="followedCount"> · {{ followedCount }} partner seguiti</template>
        </span>
      </div>
    </div>

    <!-- ------------------------- creazione / modifica ---------------------- -->
    <el-dialog
      v-model="dialog"
      :title="draft.id ? 'Modifica advisor' : 'Nuovo advisor'"
      width="580px"
      class="adv-dialog"
    >
      <div class="adv__form">
        <div class="adv__grid2">
          <div class="adv__fld">
            <label for="adv-name">Nome</label>
            <el-input
              id="adv-name"
              v-model="draft.name"
            />
          </div>
          <div class="adv__fld">
            <label for="adv-surname">Cognome</label>
            <el-input
              id="adv-surname"
              v-model="draft.surname"
            />
          </div>
        </div>
        <div class="adv__grid2">
          <div class="adv__fld">
            <label for="adv-mail">Email</label>
            <el-input
              id="adv-mail"
              v-model="draft.mail"
            />
          </div>
          <div class="adv__fld">
            <label for="adv-phone">Telefono <span class="adv__opt">(facoltativo)</span></label>
            <el-input
              id="adv-phone"
              v-model="draft.phone_number"
            />
          </div>
        </div>
        <div class="adv__fld">
          <label for="adv-commission">Provvigione mensile</label>
          <div class="adv__inline">
            <el-input-number
              id="adv-commission"
              v-model="draft.commission_percent"
              :min="0"
              :max="100"
              :precision="2"
              :step="0.5"
              controls-position="right"
            />
            <span class="adv__help">% su tutto quello che producono i partner seguiti.</span>
          </div>
        </div>
        <div class="adv__fld">
          <label for="adv-partners">Partner seguiti</label>
          <el-select
            id="adv-partners"
            v-model="draft.partnerIds"
            multiple
            filterable
            placeholder="Scegli i partner"
            style="width: 100%"
            :loading="partnersLoading"
          >
            <el-option
              v-for="p in partnerOptions"
              :key="p.id"
              :label="p.company_name"
              :value="p.id"
            />
          </el-select>
        </div>
        <p
          v-if="formProblem && tried"
          class="adv__err"
          role="alert"
        >
          {{ formProblem }}
        </p>
      </div>
      <template #footer>
        <el-button @click="dialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="save"
        >
          Salva
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import advisorAPI from '../../services/advisorAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const emptyDraft = () => ({
  id: '', name: '', surname: '', mail: '', phone_number: '', commission_percent: 0, partnerIds: [],
});

export default {
  name: 'AdvisorPage',
  components: { StatusPill },

  setup() {
    const rows = ref([]);
    const loading = ref(false);
    const forbidden = ref(false);
    const search = ref('');
    const segment = ref('tutti'); // tutti | attivi | disattivati
    const dialog = ref(false);
    const saving = ref(false);
    const tried = ref(false);
    const busyId = ref('');
    const draft = reactive(emptyDraft());
    const partnerOptions = ref([]);
    const partnersLoading = ref(false);

    const kpis = computed(() => {
      const known = !forbidden.value;
      const active = rows.value.filter((r) => r.is_active).length;
      return [
        { key: 'tutti', label: 'Advisor', value: known ? rows.value.length : null },
        { key: 'attivi', label: 'Attivi', value: known ? active : null },
        { key: 'disattivati', label: 'Disattivati', value: known ? rows.value.length - active : null },
      ];
    });

    const visibleRows = computed(() => {
      if (segment.value === 'attivi') return rows.value.filter((r) => r.is_active);
      if (segment.value === 'disattivati') return rows.value.filter((r) => !r.is_active);
      return rows.value;
    });

    const followedCount = computed(
      () => new Set(visibleRows.value.flatMap((r) => (r.partners || []).map((p) => p.id))).size,
    );

    const emptyText = computed(() => {
      if (forbidden.value) return 'Sezione riservata a Simba.';
      if (search.value.trim()) return 'Nessun advisor corrisponde alla ricerca.';
      if (segment.value === 'attivi') return 'Nessun advisor attivo.';
      if (segment.value === 'disattivati') return 'Nessun advisor disattivato.';
      return 'Nessun advisor registrato.';
    });

    const load = async () => {
      loading.value = true;
      try {
        rows.value = await advisorAPI.getAdvisors({ search: search.value });
        forbidden.value = false;
      } catch (e) {
        forbidden.value = e?.response?.status === 403;
        if (!forbidden.value) {
          console.error('Advisor non caricati:', e);
          ElMessage.error('Impossibile caricare gli advisor');
        }
        rows.value = [];
      } finally {
        loading.value = false;
      }
    };

    let timer = null;
    const onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(load, 350);
    };

    // L'elenco partner serve solo al dialogo: si chiede la prima volta che si apre.
    const loadPartnerOptions = async () => {
      if (partnerOptions.value.length || partnersLoading.value) return;
      partnersLoading.value = true;
      try {
        partnerOptions.value = await advisorAPI.getPartnerOptions();
      } catch (e) {
        console.error('Partner non caricati:', e);
        ElMessage.error('Elenco partner non disponibile');
      } finally {
        partnersLoading.value = false;
      }
    };

    const openNew = () => {
      Object.assign(draft, emptyDraft());
      tried.value = false;
      dialog.value = true;
      loadPartnerOptions();
    };

    const openEdit = (row) => {
      Object.assign(draft, {
        id: row.id,
        name: row.name || '',
        surname: row.surname || '',
        mail: row.mail || '',
        phone_number: row.phone_number || '',
        commission_percent: Number(row.commission_percent) || 0,
        partnerIds: (row.partners || []).map((p) => p.id),
      });
      tried.value = false;
      dialog.value = true;
      loadPartnerOptions();
    };

    const formProblem = computed(() => {
      if (!draft.name.trim()) return 'Manca il nome';
      if (!draft.surname.trim()) return 'Manca il cognome';
      if (!EMAIL_RE.test(draft.mail.trim())) return 'Email non valida';
      return '';
    });

    const save = async () => {
      tried.value = true;
      if (formProblem.value) return;
      saving.value = true;
      const body = {
        name: draft.name.trim(),
        surname: draft.surname.trim(),
        mail: draft.mail.trim(),
        phone_number: draft.phone_number.trim() || null,
        commission_percent: Number(draft.commission_percent) || 0,
        partners: draft.partnerIds.map((id) => ({ id })),
      };
      try {
        if (draft.id) await advisorAPI.updateAdvisor(draft.id, body);
        else await advisorAPI.createAdvisor({ ...body, is_active: true });
        ElMessage.success(draft.id ? 'Advisor aggiornato.' : 'Advisor creato.');
        dialog.value = false;
        await load();
      } catch (e) {
        console.error('Salvataggio advisor fallito:', e);
        ElMessage.error(
          e?.response?.status === 403
            ? 'Operazione riservata ai ruoli globali.'
            : 'Salvataggio non riuscito',
        );
      } finally {
        saving.value = false;
      }
    };

    const toggleActive = async (row) => {
      busyId.value = row.id;
      try {
        await advisorAPI.setAdvisorActive(row.id, !row.is_active);
        await load();
      } catch (e) {
        console.error('Cambio stato fallito:', e);
        ElMessage.error('Cambio stato non riuscito');
      } finally {
        busyId.value = '';
      }
    };

    // ---- helper di presentazione -------------------------------------
    const fullName = (r) => [r?.name, r?.surname].filter(Boolean).join(' ') || '—';

    const initials = (r) =>
      [r?.name, r?.surname]
        .filter(Boolean)
        .map((w) => w.trim()[0])
        .join('')
        .toUpperCase() || '?';

    const percent = (v) => `${Number(v || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`;

    onMounted(load);

    return {
      rows, visibleRows, loading, forbidden, search, segment, kpis, followedCount, emptyText,
      dialog, draft, saving, tried, busyId, partnerOptions, partnersLoading, formProblem,
      onSearchInput, openNew, openEdit, save, toggleActive, fullName, initials, percent,
    };
  },
};
</script>

<style scoped>
.adv {
  padding: 4px 0 32px;
}

/* ---- intestazione ---- */
.adv__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.adv__title {
  margin: 0;
  font-size: 22px;
  color: var(--text-primary);
}
.adv__sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--text-tertiary);
}
.adv__headActions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.adv__search {
  width: 280px;
}

.adv__warn {
  margin-bottom: 14px;
  padding: 10px 13px;
  border-radius: 9px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--state-attn);
  background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}

/* ---- KPI cliccabili (fanno anche da filtro dello stato) ---- */
.adv__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
@media (max-width: 720px) {
  .adv__kpis { grid-template-columns: 1fr; }
}
.adv__kpi {
  text-align: left;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 13px 16px;
  cursor: pointer;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  font-family: inherit;
}
.adv__kpi:hover { border-color: var(--brand-violet); }
.adv__kpi.is-active {
  border-color: var(--brand-violet);
  box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.adv__kpiNum {
  display: block;
  font-size: 24px;
  font-weight: 750;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}
.adv__kpi.is-muted .adv__kpiNum { color: var(--state-muted); }
.adv__kpiLbl {
  display: block;
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

/* ---- tabella ---- */
.adv__card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.adv__who {
  display: flex;
  align-items: center;
  gap: 10px;
}
.adv__avatar {
  width: 32px;
  height: 32px;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--avatar-bg);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.adv__name { font-weight: 600; color: var(--text-primary); }
.adv__meta { font-size: 12px; color: var(--text-tertiary); }
.adv__num { font-variant-numeric: tabular-nums; }
.adv__strong { font-weight: 700; color: var(--text-primary); }
.adv__dash { color: var(--text-tertiary); }
.adv__chips { display: flex; flex-wrap: wrap; gap: 5px; }
.adv__chip {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 1px 9px;
}
.adv__chip--more { color: var(--text-tertiary); }

/* ---- piede ---- */
.adv__foot {
  padding: 12px 14px;
  border-top: 1px solid var(--border-color);
}
.adv__footInfo {
  font-size: 12.5px;
  color: var(--text-tertiary);
}

/* la riga apre la modifica: lo si deve capire */
.adv :deep(.el-table__row) { cursor: pointer; }

/* Intestazione della tabella grigio chiaro come nella lista Clienti:
   element-overrides.css impone a tutte le tabelle il gradiente viola con
   !important, quindi serve !important anche qui. */
.adv :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 11px 12px;
  border-bottom: 1px solid var(--border-color);
}

/* ---- dialogo ---- */
.adv__form { display: flex; flex-direction: column; gap: 14px; }
.adv__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 560px) {
  .adv__grid2 { grid-template-columns: 1fr; }
}
.adv__fld { display: flex; flex-direction: column; min-width: 0; }
.adv__fld label {
  display: block;
  margin-bottom: 5px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.adv__inline { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.adv__inline :deep(.el-input-number) { width: 170px; }
.adv__opt { text-transform: none; letter-spacing: 0; font-weight: 500; }
.adv__help { font-size: 12px; color: var(--text-tertiary); }
.adv__err { margin: 0; font-size: 12.5px; font-weight: 600; color: var(--state-attn); }
</style>
